import { Component, OnDestroy } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from './_services/auth.service';
import { User } from './_interfaces/user';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY, Subject } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { FeatureFlagsService } from './_services/feature-flags.service';
import { Language } from './_enums/language';
import { i18nLocale } from './_helpers/utils';
import { StateMonitorComponent } from './shared/components/state-monitor/state-monitor.component';
import { DevelopmentModeService } from './_services/development-mode.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, TranslateModule, StateMonitorComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnDestroy {
  showDevelopmentModePanel = false;
  currentUser: User | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private flags: FeatureFlagsService,
    private developmentModeService: DevelopmentModeService,
  ) {
    this.translate.addLangs(['Medical-en', 'Medical-de', 'Legal-en', 'Legal-de']);
    this.translate.setFallbackLang('Medical-en');

    const browserLang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en';
    const langCode = ['en', 'de'].includes(browserLang?.split('-')[0]) ? browserLang.split('-')[0] : 'en';
    this.translate.use(`Medical-${langCode}`);

    this.authService.user$.subscribe(user => {
      this.currentUser = user;
      if (user) {
        const category = user.entity?.settings?.category;
        const browserLang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : null;
        const browserLangCode = browserLang?.split('-')[0]?.toLowerCase();

        if (browserLangCode && ['en', 'de'].includes(browserLangCode)) {
          const lang = browserLangCode === 'de' ? Language.German : Language.English;
          this.translate.use(i18nLocale(category, lang));
        } else if (user.entity?.settings?.language) {
          this.translate.use(i18nLocale(category, user.entity.settings.language));
        }

        const region = this.authService.getAuth()?.environment || 'LO';
        this.flags.getClient()?.updateContext({ userId: region + '-' + user.id.toString(), properties: { entityId: user.entity_id.toString(), region } });
      }
    });

    this.flags.on('ready', () => {
      console.log('Feature flags ready');
      this.updateDevelopmentModePanelVisibility();
    });
    this.flags.on('error', (error) => {
      console.error('Feature flags error', error);
    });
    this.flags.on('update', (event) => {
      console.log('Feature flags update', event);
      this.updateDevelopmentModePanelVisibility();
    });
    this.developmentModeService.active$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateDevelopmentModePanelVisibility());
    this.updateDevelopmentModePanelVisibility();
    this.registerSvgIcons();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private registerSvgIcons(): void {
    const iconDefinitions = [
      { name: 'ai-blueprints', url: 'assets/doc-star.svg' },
      { name: 'book-stack', url: 'assets/book-stack.svg' },
      { name: 'building', url: 'assets/icon-building.svg' },
      { name: 'check', url: 'assets/check.svg' },
      { name: 'check-circle', url: 'assets/check-circle.svg' },
      { name: 'close-affordance', url: 'assets/close-affordance.svg' },
      { name: 'community', url: 'assets/community.svg' },
      { name: 'connection-unstable', url: 'assets/icon-connection-unstable.svg' },
      { name: 'copilot', url: 'assets/copilot.svg' },
      { name: 'dialog', url: 'assets/icon-dialog.svg' },
      { name: 'design-nib', url: 'assets/design-nib.svg'},
      { name: 'edit-pencil', url: 'assets/edit-pencil.svg' },
      { name: 'encounters', url: 'assets/encounters.svg' },
      { name: 'eye_closed', url: 'assets/eye_closed.svg' },
      { name: 'eye_opened', url: 'assets/eye_opened.svg' },
      { name: 'folder_empty', url: 'assets/folder_empty.svg' },
      { name: 'home', url: 'assets/home.svg' },
      { name: 'info-circle', url: 'assets/info-circle.svg' },
      { name: 'left-bracket', url: 'assets/left-bracket.svg' },
      { name: 'logo-compact', url: 'assets/logo-compact.svg' },
      { name: 'medium', url: 'assets/medium.svg' },
      { name: 'menu', url: 'assets/menu.svg' },
      { name: 'microphone', url: 'assets/microphone.svg' },
      { name: 'microphone-mute', url: 'assets/microphone-mute.svg' },
      { name: 'microphone-speaking', url: 'assets/microphone-speaking.svg' },
      { name: 'more-vert', url: 'assets/more-vert.svg' },
      { name: 'multi-bubble', url: 'assets/multi-bubble.svg' },
      { name: 'nav-arrow-left', url: 'assets/nav-arrow-left.svg' },
      { name: 'nav-arrow-right', url: 'assets/nav-arrow-right.svg' },
      { name: 'page-flip', url: 'assets/page-flip.svg' },
      { name: 'plus', url: 'assets/plus.svg' },
      { name: 'refresh', url: 'assets/refresh.svg' },
      { name: 'refresh-double', url: 'assets/refresh-double.svg' },
      { name: 'right-bracket', url: 'assets/right-bracket.svg' },
      { name: 'search', url: 'assets/icon-search.svg' },
      { name: 'settings', url: 'assets/settings.svg' },
      { name: 'share-ios', url: 'assets/share-ios.svg' },
      { name: 'sparks', url: 'assets/sparks.svg' },
      { name: 'speechwrite-icon', url: 'assets/speechwrite_icon.svg' },
      { name: 'support', url: 'assets/support.svg' },
      { name: 'thumbs-down', url: 'assets/thumbs-down.svg' },
      { name: 'thumbs-down-fill', url: 'assets/thumbs-down-fill.svg' },
      { name: 'thumbs-up', url: 'assets/thumbs-up.svg' },
      { name: 'thumbs-up-fill', url: 'assets/thumbs-up-fill.svg' },
      { name: 'update-document', url: 'assets/icon-update-document.svg' },
      { name: 'user-circle', url: 'assets/user-circle.svg' },
      { name: 'warning-circle', url: 'assets/warning-circle.svg' },
      { name: 'warning-triangle', url: 'assets/warning-triangle.svg' },
    ];
    for (const { name, url } of iconDefinitions) {
      this.iconRegistry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(url));
    }
    this.preloadSvgIcons(iconDefinitions.map(({ name }) => name));
  }

  private preloadSvgIcons(iconNames: string[]): void {
    for (const name of iconNames) {
      this.iconRegistry
        .getNamedSvgIcon(name)
        .pipe(
          take(1),
          catchError((error) => {
            console.warn(`Failed to preload SVG icon "${name}"`, error);
            return EMPTY;
          }),
        )
        .subscribe();
    }
  }

  private updateDevelopmentModePanelVisibility(): void {
    if (!this.flags.isEnabled('developmentMode')) {
      this.showDevelopmentModePanel = false;
      return;
    }

    this.showDevelopmentModePanel = this.developmentModeService.isActive();
  }
}
